'use client'

import { memo, useMemo } from 'react'

import { TMSDeliverySlotsArraySlot } from 'modules/delivery'
import Button from 'shared/ui/Button'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'

import styles from './ReservationConfirmModal.module.scss'

interface ReservationConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  selectedSlots: TMSDeliverySlotsArraySlot[]
  selectedDate: string | null
  isLoading: boolean
}

// Utility function for date formatting
const formatDateWithWeekday = (date: Date): string => {
  const formattedDate = date.toLocaleDateString('pl-PL')
  const weekday = date.toLocaleDateString('pl-PL', { weekday: 'long' })
  const formattedWeekday = weekday[0].toUpperCase() + weekday.slice(1)

  return `${formattedDate} (${formattedWeekday})`
}

const ReservationConfirmModal: React.FC<ReservationConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedSlots,
  selectedDate,
  isLoading
}) => {
  const modalData = useMemo(() => {
    if (!selectedDate || selectedSlots.length === 0) {
      return null
    }

    const sortedSlots = [...selectedSlots].sort((a, b) => {
      const [, aEnd] = a.time.split('-')
      const [, bEnd] = b.time.split('-')

      return parseInt(aEnd.replace(':', '')) - parseInt(bEnd.replace(':', ''))
    })

    const from = sortedSlots[0].time.split('-')[0]
    const to = sortedSlots[sortedSlots.length - 1].time.split('-')[1]

    return {
      formattedDate: formatDateWithWeekday(new Date(selectedDate)),
      from,
      to
    }
  }, [selectedDate, selectedSlots])

  if (!modalData) {
    return null
  }

  return (
    <Modal open={isOpen} onClose={onClose} id={'confirm-reservation-modal'}>
      <ModalTitle>Potwierdzenie rezerwacji</ModalTitle>
      <div className={styles.modalContent}>
        <div className={styles.reservationDetails}>
          <div className={styles.detailRow}>
            <span className={styles.label}>Data dostawy:</span>
            <span className={styles.value}>{modalData.formattedDate}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.label}>Wybrane godziny:</span>
            <span className={styles.value}>
              {modalData.from} - {modalData.to}
            </span>
          </div>
        </div>

        <ModalDescription>
          Czy na pewno chcesz zarezerwować termin dostawy na wybrany dzień i
          godziny?
        </ModalDescription>

        <div className={styles.buttonsWrapper}>
          <Button
            variant={ButtonVariants.neutralOutline}
            onClick={onClose}
            fullWidth
            disabled={isLoading}
          >
            Cofnij
          </Button>
          <Button
            variant={ButtonVariants.primary}
            onClick={onConfirm}
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            Potwierdź rezerwację
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default memo(ReservationConfirmModal)
